import React, { useRef, useState } from 'react';
import { X, Download, Printer } from 'lucide-react';
import { PrintableCoupon, PrintableCouponPage, PrintableCouponBack, PrintableCouponData } from './PrintableCoupon';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface CouponPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  coupons: PrintableCouponData[];
  onConfirmGenerate: () => Promise<void>;
  themeName: string;
}

export const CouponPreviewModal: React.FC<CouponPreviewModalProps> = ({
  isOpen,
  onClose,
  coupons,
  onConfirmGenerate,
  themeName,
}) => {
  const printRef = useRef<HTMLDivElement>(null);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  // Group coupons into pages of 8
  const pages: PrintableCouponData[][] = [];
  for (let i = 0; i < coupons.length; i += 8) {
    pages.push(coupons.slice(i, i + 8));
  }

  const generatePDF = async () => {
    if (!printRef.current) return;

    // Create PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'in',
      format: 'letter',
    });

    const pageElements = printRef.current.querySelectorAll('.printable-coupon-page');
    
    for (let i = 0; i < pageElements.length; i++) {
      const pageElement = pageElements[i] as HTMLElement;
      
      const canvas = await html2canvas(pageElement, {
        scale: 2, // Higher quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
      });

      const imgData = canvas.toDataURL('image/png');
      
      if (i > 0) {
        pdf.addPage();
      }
      
      // Add image to fill the page
      pdf.addImage(imgData, 'PNG', 0, 0, 8.5, 11);
    }

    // Save the PDF
    const fileName = `coupons-${themeName.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);
  };

  const handleGeneratePDF = async () => {
    setGenerating(true);
    setError(null);
    
    try {
      // First, save coupons to database
      await onConfirmGenerate();
      setGenerated(true);

      // Then generate PDF
      await generatePDF();
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate PDF. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadAgain = async () => {
    setGenerating(true);
    setError(null);
    try {
      await generatePDF();
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate PDF. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex flex-col justify-end sm:items-center sm:justify-center bg-black bg-opacity-50"
         onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-6xl max-h-[90vh] sm:max-h-[90vh] sm:mx-4 overflow-hidden flex flex-col">
        <div className="sm:hidden flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Coupon Preview</h2>
              <p className="text-sm text-gray-500 mt-1">
                {coupons.length} coupon{coupons.length !== 1 ? 's' : ''} • {pages.length * 2} page{pages.length * 2 !== 1 ? 's' : ''} (front & back)
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Error message */}
          {error && (
            <div className="mx-6 mt-4 p-4 bg-red-50 text-red-700 rounded-md">
              {error}
            </div>
          )}

          {/* Preview Area */}
          <div className="flex-1 overflow-auto p-6 bg-gray-100">
            <div className="space-y-8">
              {pages.map((pageCoupons, pageIndex) => (
                <React.Fragment key={pageIndex}>
                  {/* Front page */}
                  <div className="bg-white shadow-lg mx-auto rounded-lg overflow-hidden" style={{ width: 'fit-content' }}>
                    <div className="text-center py-2 bg-gray-50 border-b text-sm text-gray-500">
                      Page {pageIndex * 2 + 1} of {pages.length * 2} (Front)
                    </div>
                    <div
                      className="p-4"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '8px',
                      }}
                    >
                      {pageCoupons.map((coupon, couponIndex) => (
                        <PrintableCoupon key={couponIndex} data={coupon} />
                      ))}
                    </div>
                  </div>
                  {/* Back page */}
                  <div className="bg-white shadow-lg mx-auto rounded-lg overflow-hidden" style={{ width: 'fit-content' }}>
                    <div className="text-center py-2 bg-gray-50 border-b text-sm text-gray-500">
                      Page {pageIndex * 2 + 2} of {pages.length * 2} (Back)
                    </div>
                    <div
                      className="p-4"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '8px',
                      }}
                    >
                      {pageCoupons.map((coupon, couponIndex) => (
                        <PrintableCouponBack key={couponIndex} data={coupon} />
                      ))}
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Hidden print area for PDF generation */}
          <div
            ref={printRef}
            style={{
              position: 'absolute',
              left: '-9999px',
              top: '0',
              width: '8.5in',
              backgroundColor: 'white',
            }}
          >
            {pages.map((pageCoupons, pageIndex) => (
              <React.Fragment key={pageIndex}>
                {/* Front page */}
                <PrintableCouponPage coupons={pageCoupons} showBack={false} />
                {/* Back page */}
                <PrintableCouponPage coupons={pageCoupons} showBack={true} />
              </React.Fragment>
            ))}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
            <div className="text-sm text-gray-500">
              {generated ? (
                <span className="text-green-600 font-medium">
                  ✓ Coupons saved to database
                </span>
              ) : (
                'Coupons will be saved to database when you generate PDF'
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                {generated ? 'Close' : 'Cancel'}
              </button>
              {generated ? (
                <button
                  onClick={handleDownloadAgain}
                  disabled={generating}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Download PDF Again
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={handleGeneratePDF}
                  disabled={generating}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving & Generating...
                    </>
                  ) : (
                    <>
                      <Printer className="w-4 h-4" />
                      Save & Generate PDF
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
  );
};
